import React, { useState, useEffect, useMemo, useCallback } from "react";
import { useParams } from "react-router-dom";

import {
  Badge,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  InputAdornment,
  TextField,
  Typography,
  alpha,
} from "@mui/material";
import {
  Search,
  ShoppingCart,
  Add,
  Remove,
  Delete,
  Storefront,
  DeliveryDining,
  WhatsApp,
  Instagram,
  Facebook,
  YouTube,
  Close,
} from "@mui/icons-material";

import { openApi } from "../../services/api";
import { getBackendUrl } from "../../config";
import AgendarModal from "./AgendarModal";

const brl = (v) =>
  Number(v || 0).toLocaleString("pt-BR", { style: "currency", currency: "BRL" });

const SEM_CATEGORIA = "__outros__";

const Loja = () => {
  const { slug } = useParams();

  const [store, setStore] = useState(null);
  const [categories, setCategories] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  const [search, setSearch] = useState("");
  const [cart, setCart] = useState([]);
  const [cartOpen, setCartOpen] = useState(false);
  const [descOpen, setDescOpen] = useState(false);

  const cartKey = `cart_${slug}`;

  // Servicos agendaveis: os que tem duracao e alguem para atender.
  const [agendaveis, setAgendaveis] = useState([]);
  const [agendando, setAgendando] = useState(null);

  // --- carrega a loja (openApi: pagina publica, sem sessao) ---
  useEffect(() => {
    setLoading(true);
    openApi
      .request({ url: `/store/${slug}`, method: "GET" })
      .then(({ data }) => {
        setStore(data.store);
        setCategories(data.categories || []);
        setProducts(data.products || []);
      })
      .catch(() => setNotFound(true))
      .finally(() => setLoading(false));

    // Falha aqui nao derruba a vitrine: sem agenda, os produtos continuam
    // a venda normalmente.
    openApi
      .request({ url: `/store/${slug}/services`, method: "GET" })
      .then(({ data }) => setAgendaveis(data || []))
      .catch(() => setAgendaveis([]));
  }, [slug]);

  // --- carrinho vive no navegador do cliente; nada e gravado no servidor ---
  useEffect(() => {
    try {
      const saved = localStorage.getItem(cartKey);
      if (saved) setCart(JSON.parse(saved));
    } catch {
      setCart([]);
    }
  }, [cartKey]);

  const persist = useCallback(
    (next) => {
      setCart(next);
      try {
        localStorage.setItem(cartKey, JSON.stringify(next));
      } catch {
        // localStorage cheio/bloqueado nao pode quebrar a loja
      }
    },
    [cartKey]
  );

  const addToCart = (p) => {
    if (p.soldOut) return;
    const found = cart.find((i) => i.id === p.id);
    persist(
      found
        ? cart.map((i) => (i.id === p.id ? { ...i, qty: i.qty + 1 } : i))
        : [...cart, { id: p.id, name: p.name, price: p.effectivePrice, qty: 1 }]
    );
  };

  const changeQty = (id, delta) => {
    const next = cart
      .map((i) => (i.id === id ? { ...i, qty: i.qty + delta } : i))
      .filter((i) => i.qty > 0);
    persist(next);
  };

  const removeItem = (id) => persist(cart.filter((i) => i.id !== id));

  const total = cart.reduce((s, i) => s + i.price * i.qty, 0);
  const count = cart.reduce((s, i) => s + i.qty, 0);

  const imgUrl = (file, folder) =>
    file && store
      ? `${getBackendUrl()}/public/company${store.companyId}/${folder}/${file}`
      : null;

  // --- agrupa por categoria, respeitando a ordem vinda do backend ---
  const grupos = useMemo(() => {
    const filtrados = products.filter((p) =>
      p.name?.toLowerCase().includes(search.toLowerCase())
    );

    const out = [];
    categories.forEach((c) => {
      const itens = filtrados.filter((p) => p.categoryId === c.id);
      if (itens.length) out.push({ key: c.id, nome: c.name, itens });
    });

    const semCat = filtrados.filter(
      (p) => !p.categoryId || !categories.some((c) => c.id === p.categoryId)
    );
    if (semCat.length) out.push({ key: SEM_CATEGORIA, nome: "Outros", itens: semCat });

    return out;
  }, [products, categories, search]);

  const finalizar = () => {
    if (!cart.length || !store?.whatsappNumber) return;
    const linhas = cart
      .map((i) => `${i.qty}x ${i.name} — ${brl(i.price * i.qty)}`)
      .join("\n");
    const msg = `Olá! Gostaria de fazer este pedido:\n\n${linhas}\n\n*Total: ${brl(total)}*`;
    const numero = String(store.whatsappNumber).replace(/\D/g, "");
    window.open(`https://wa.me/${numero}?text=${encodeURIComponent(msg)}`, "_blank");
  };

  if (loading) {
    return (
      <Box sx={{ minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center" }}>
        <CircularProgress />
      </Box>
    );
  }

  // Loja inexistente e loja desligada caem aqui igual — o backend nao diferencia.
  if (notFound || !store) {
    return (
      <Box
        sx={{
          minHeight: "100vh",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          gap: 1,
          p: 3,
          textAlign: "center",
        }}
      >
        <Storefront sx={{ fontSize: 56, color: "text.disabled" }} />
        <Typography variant="h6" sx={{ fontWeight: 700 }}>
          Loja não encontrada
        </Typography>
        <Typography sx={{ color: "text.secondary", fontSize: 14 }}>
          O endereço pode estar errado ou a loja não está disponível no momento.
        </Typography>
      </Box>
    );
  }

  const cor = store.primaryColor || "#1B7A5A";
  const banner = imgUrl(store.bannerUrl, "store");
  const logo = imgUrl(store.logoUrl, "store");
  const descCurta = (store.description || "").length > 120;

  const redes = [
    { url: store.whatsappNumber ? `https://wa.me/${String(store.whatsappNumber).replace(/\D/g, "")}` : null, icon: <WhatsApp />, bg: "#25D366" },
    { url: store.instagramUrl, icon: <Instagram />, bg: "#E1306C" },
    { url: store.facebookUrl, icon: <Facebook />, bg: "#1877F2" },
    { url: store.youtubeUrl, icon: <YouTube />, bg: "#FF0000" },
  ].filter((r) => r.url);

  return (
    <Box sx={{ minHeight: "100vh", bgcolor: "#f5f6f7", pb: 11 }}>
      {/* ---------- HEADER ---------- */}
      <Box sx={{ bgcolor: cor, color: "#fff", px: 2, pt: 2, pb: 2.5, borderRadius: "0 0 18px 18px" }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 1.5 }}>
          {logo && (
            <Box
              component="img"
              src={logo}
              alt={store.title}
              sx={{ width: 46, height: 46, borderRadius: "50%", objectFit: "cover", bgcolor: "#fff" }}
            />
          )}
          <Typography sx={{ fontWeight: 800, fontSize: 19, lineHeight: 1.15 }}>
            {store.title}
          </Typography>
        </Box>

        <TextField
          fullWidth
          size="small"
          placeholder="Busque por um item na loja..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          sx={{
            bgcolor: "#fff",
            borderRadius: 999,
            "& fieldset": { border: "none" },
            "& .MuiOutlinedInput-root": { borderRadius: 999 },
          }}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <Search sx={{ color: cor }} />
              </InputAdornment>
            ),
          }}
        />
      </Box>

      {/* ---------- BANNER ---------- */}
      {banner && (
        <Box sx={{ px: 2, mt: 2 }}>
          <Box
            component="img"
            src={banner}
            alt="banner"
            sx={{ width: "100%", borderRadius: 2, display: "block", maxHeight: 170, objectFit: "cover" }}
          />
        </Box>
      )}

      {/* ---------- STATUS ---------- */}
      <Box sx={{ px: 2, mt: 2, display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
        <Chip
          size="small"
          label={store.isOpen ? "Aberto" : "Fechado"}
          sx={{
            fontWeight: 700,
            color: "#fff",
            bgcolor: store.isOpen ? "#2e7d32" : "#c62828",
          }}
        />
        {store.deliveryEnabled && (
          <Chip size="small" icon={<DeliveryDining sx={{ fontSize: 16 }} />} label="Fazemos entrega" variant="outlined" />
        )}
      </Box>

      {/* ---------- DESCRICAO ---------- */}
      {store.description && (
        <Box sx={{ px: 2, mt: 1.5, textAlign: "center" }}>
          <Typography sx={{ fontSize: 13.5, color: "text.secondary" }}>
            {descCurta ? `${store.description.slice(0, 120)}...` : store.description}
          </Typography>
          {descCurta && (
            <Button size="small" onClick={() => setDescOpen(true)} sx={{ color: cor, textTransform: "none" }}>
              Ver descrição completa
            </Button>
          )}
        </Box>
      )}

      {/* ---------- PRODUTOS ---------- */}
      <Box sx={{ px: 2, mt: 2 }}>
        {grupos.length === 0 && (
          <Typography sx={{ textAlign: "center", color: "text.secondary", py: 6 }}>
            {search ? "Nenhum item encontrado para essa busca." : "Esta loja ainda não tem itens."}
          </Typography>
        )}

        {grupos.map((g) => (
          <Box key={g.key} sx={{ mb: 3 }}>
            <Typography sx={{ fontWeight: 800, fontSize: 17, mb: 1.2 }}>{g.nome}</Typography>

            <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 1.5 }}>
              {g.itens.map((p) => {
                const foto = imgUrl(p.imageUrl, "products");
                const off = p.isOffer && p.price > 0
                  ? Math.round((1 - p.effectivePrice / p.price) * 100)
                  : 0;

                return (
                  <Box
                    key={p.id}
                    sx={{
                      bgcolor: "#fff",
                      borderRadius: 2,
                      overflow: "hidden",
                      position: "relative",
                      opacity: p.soldOut ? 0.55 : 1,
                      boxShadow: "0 1px 6px rgba(0,0,0,.06)",
                    }}
                  >
                    {off > 0 && (
                      <Chip
                        size="small"
                        label={`${off}% OFF`}
                        sx={{
                          position: "absolute", top: 8, left: 8, zIndex: 1,
                          bgcolor: cor, color: "#fff", fontWeight: 700, fontSize: 11,
                        }}
                      />
                    )}

                    {p.soldOut && (
                      <Chip
                        size="small"
                        label="Esgotado"
                        sx={{
                          position: "absolute", top: 8, right: 8, zIndex: 1,
                          bgcolor: "#c62828", color: "#fff", fontWeight: 700, fontSize: 11,
                        }}
                      />
                    )}

                    {foto ? (
                      <Box component="img" src={foto} alt={p.name} sx={{ width: "100%", height: 118, objectFit: "cover", display: "block" }} />
                    ) : (
                      <Box sx={{ width: "100%", height: 118, bgcolor: (t) => alpha(t.palette.text.primary, 0.05), display: "flex", alignItems: "center", justifyContent: "center" }}>
                        <Storefront sx={{ opacity: 0.3 }} />
                      </Box>
                    )}

                    <Box sx={{ p: 1.2 }}>
                      <Typography sx={{ fontSize: 13.5, fontWeight: 600, lineHeight: 1.25, minHeight: 34 }}>
                        {p.name}
                      </Typography>

                      <Box sx={{ display: "flex", alignItems: "center", gap: 0.7, mt: 0.4 }}>
                        {off > 0 && (
                          <Typography sx={{ fontSize: 11.5, textDecoration: "line-through", color: "text.disabled" }}>
                            {brl(p.price)}
                          </Typography>
                        )}
                        <Typography sx={{ fontWeight: 800, fontSize: 15, color: off > 0 ? "success.main" : "text.primary" }}>
                          {brl(p.effectivePrice)}
                        </Typography>
                      </Box>

                      {agendaveis.find((a) => a.id === p.id) ? (
                        /* Servico agendavel: "Agendar" SUBSTITUI "Adicionar".
                           Sao fluxos opostos -- o carrinho so manda mensagem,
                           o agendamento reserva um horario de verdade. */
                        <Button
                          fullWidth
                          size="small"
                          disabled={!store.isOpen}
                          onClick={() =>
                            setAgendando(agendaveis.find((a) => a.id === p.id))
                          }
                          sx={{
                            mt: 1, borderRadius: 999, textTransform: "none", fontWeight: 700,
                            bgcolor: cor, color: "#fff",
                            "&:hover": { bgcolor: cor, opacity: 0.9 },
                            "&.Mui-disabled": { bgcolor: "#e0e0e0", color: "#9e9e9e" },
                          }}
                        >
                          Agendar
                        </Button>
                      ) : (
                        <Button
                          fullWidth
                          size="small"
                          disabled={p.soldOut || !store.isOpen}
                          onClick={() => addToCart(p)}
                          sx={{
                            mt: 1, borderRadius: 999, textTransform: "none", fontWeight: 700,
                            bgcolor: cor, color: "#fff",
                            "&:hover": { bgcolor: cor, opacity: 0.9 },
                            "&.Mui-disabled": { bgcolor: "#e0e0e0", color: "#9e9e9e" },
                          }}
                        >
                          {p.soldOut ? "Esgotado" : "Adicionar"}
                        </Button>
                      )}
                    </Box>
                  </Box>
                );
              })}
            </Box>
          </Box>
        ))}
      </Box>

      {/* ---------- REDES FLUTUANTES ---------- */}
      <Box sx={{ position: "fixed", right: 10, top: "45%", display: "flex", flexDirection: "column", gap: 1, zIndex: 5 }}>
        {redes.map((r, i) => (
          <IconButton
            key={i}
            onClick={() => window.open(r.url, "_blank")}
            sx={{ bgcolor: r.bg, color: "#fff", width: 38, height: 38, "&:hover": { bgcolor: r.bg, opacity: 0.9 } }}
          >
            {r.icon}
          </IconButton>
        ))}
      </Box>

      {/* ---------- BARRA DO CARRINHO ---------- */}
      {count > 0 && (
        <Box
          sx={{
            position: "fixed", bottom: 0, left: 0, right: 0, zIndex: 10,
            bgcolor: "#fff", borderTop: "1px solid #eee", p: 1.5,
            display: "flex", alignItems: "center", gap: 1.5,
          }}
        >
          <Badge badgeContent={count} color="error">
            <ShoppingCart sx={{ color: cor }} />
          </Badge>
          <Box sx={{ flex: 1 }}>
            <Typography sx={{ fontSize: 11, color: "text.secondary", lineHeight: 1 }}>Total</Typography>
            <Typography sx={{ fontWeight: 800, fontSize: 16 }}>{brl(total)}</Typography>
          </Box>
          <Button
            onClick={() => setCartOpen(true)}
            sx={{ borderRadius: 999, textTransform: "none", fontWeight: 700, bgcolor: cor, color: "#fff", px: 2.5, "&:hover": { bgcolor: cor, opacity: 0.9 } }}
          >
            Ver carrinho
          </Button>
        </Box>
      )}

      {/* ---------- MODAL CARRINHO ---------- */}
      <Dialog open={cartOpen} onClose={() => setCartOpen(false)} fullWidth maxWidth="xs">
        <DialogTitle sx={{ fontWeight: 800, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          Seu pedido
          <IconButton size="small" onClick={() => setCartOpen(false)}><Close /></IconButton>
        </DialogTitle>
        <DialogContent dividers>
          {cart.map((i) => (
            <Box key={i.id} sx={{ display: "flex", alignItems: "center", gap: 1, py: 1, borderBottom: "1px solid #f0f0f0" }}>
              <Box sx={{ flex: 1 }}>
                <Typography sx={{ fontSize: 14, fontWeight: 600 }}>{i.name}</Typography>
                <Typography sx={{ fontSize: 12, color: "text.secondary" }}>{brl(i.price)} cada</Typography>
              </Box>
              <IconButton size="small" onClick={() => changeQty(i.id, -1)}><Remove fontSize="small" /></IconButton>
              <Typography sx={{ fontWeight: 700, minWidth: 18, textAlign: "center" }}>{i.qty}</Typography>
              <IconButton size="small" onClick={() => changeQty(i.id, 1)}><Add fontSize="small" /></IconButton>
              <Typography sx={{ fontWeight: 700, minWidth: 62, textAlign: "right", fontSize: 13.5 }}>
                {brl(i.price * i.qty)}
              </Typography>
              <IconButton size="small" onClick={() => removeItem(i.id)}><Delete fontSize="small" sx={{ color: "#c62828" }} /></IconButton>
            </Box>
          ))}

          <Box sx={{ display: "flex", justifyContent: "space-between", mt: 2, mb: 1 }}>
            <Typography sx={{ fontWeight: 800 }}>Total</Typography>
            <Typography sx={{ fontWeight: 800 }}>{brl(total)}</Typography>
          </Box>

          {!store.isOpen && (
            <Typography sx={{ fontSize: 12.5, color: "#c62828", textAlign: "center", mb: 1 }}>
              A loja está fechada no momento.
            </Typography>
          )}
          {!store.whatsappNumber && (
            <Typography sx={{ fontSize: 12.5, color: "#c62828", textAlign: "center", mb: 1 }}>
              Esta loja está sem WhatsApp disponível para receber pedidos.
            </Typography>
          )}

          <Button
            fullWidth
            disabled={!cart.length || !store.whatsappNumber || !store.isOpen}
            onClick={finalizar}
            startIcon={<WhatsApp />}
            sx={{
              mb: 1, py: 1.2, borderRadius: 999, textTransform: "none", fontWeight: 800,
              bgcolor: "#25D366", color: "#fff",
              "&:hover": { bgcolor: "#25D366", opacity: 0.9 },
              "&.Mui-disabled": { bgcolor: "#e0e0e0", color: "#9e9e9e" },
            }}
          >
            Finalizar no WhatsApp
          </Button>
        </DialogContent>
      </Dialog>

      {/* ---------- MODAL AGENDAR ---------- */}
      {agendando && (
        <AgendarModal
          open={!!agendando}
          onClose={() => setAgendando(null)}
          slug={slug}
          service={agendando}
          storeColor={cor}
        />
      )}

      {/* ---------- MODAL DESCRICAO ---------- */}
      <Dialog open={descOpen} onClose={() => setDescOpen(false)} fullWidth maxWidth="xs">
        <DialogTitle sx={{ fontWeight: 800 }}>{store.title}</DialogTitle>
        <DialogContent dividers>
          <Typography sx={{ fontSize: 14, whiteSpace: "pre-line" }}>{store.description}</Typography>
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default Loja;
